import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { RelatedLinksSection } from "@/components/sections/RelatedLinksSection";
import { employees as staticEmployees } from "@/data/site";
import { getVisibleTeamMembers } from "@/lib/site-content-db";
import type { DbTeamMember } from "@/lib/site-content-db";
import { getTeamImage } from "@/lib/team-images";
import { Mail, Phone, MapPin, UserRound } from "lucide-react";
import hero from "@/assets/hero-omoss.webp";

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);

const Medarbetare = () => {
  const [members, setMembers] = useState<DbTeamMember[]>(
    staticEmployees.map((e, i) => ({
      id: String(i),
      name: e.name,
      role: e.role,
      featured_role: null,
      image_key: ("image" in e ? e.image : null) ?? null,
      image_url: null,
      email: ("email" in e ? e.email : null) ?? null,
      phone: ("phone" in e ? e.phone : null) ?? null,
      office: ("office" in e ? e.office : null) ?? null,
      is_featured: false,
      is_visible: true,
      sort_order: i,
    }))
  );

  useEffect(() => {
    getVisibleTeamMembers().then((data) => { if (data.length > 0) setMembers(data); });
  }, []);

  return (
    <>
      <SEO
        title="Redovisningskonsulter & lönespecialister | Cloud"
        description="Möt teamet bakom Cloud Redovisning i Umeå, Stockholm och Vännäs. Personlig service från redovisningskonsulter, lönespecialister och rådgivare."
      />
      <PageHero
        eyebrow="Medarbetare"
        title={
          <>
            Våra <span className="italic text-primary">redovisningskonsulter</span> och
            lönespecialister.
          </>
        }
        description="Vårt team i Umeå, Stockholm och Vännäs består av auktoriserade redovisningskonsulter, lönespecialister och rådgivare som hjälper företag i hela Sverige."
        image={hero}
        imageAlt="Redovisningskonsulter och lönespecialister på Cloud Redovisning"
        textColSpanClass="lg:col-span-8"
        imageColSpanClass="lg:col-span-4"
      />

      <section className="py-20 md:py-28">
        <div className="container-page">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            {members.map((m) => {
              const imgSrc = getTeamImage(m.name, m.image_key, m.image_url);
              return (
                <article key={m.id} className="group bg-card border border-border rounded-sm overflow-hidden transition-all hover:border-foreground/30 hover:shadow-soft">
                  <div className="aspect-[4/5] overflow-hidden bg-background-deep">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={m.name}
                        width={800}
                        height={1000}
                        decoding="async"
                        className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-[1.05]"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center bg-primary/10 text-primary">
                        <UserRound className="mb-5 h-12 w-12" aria-hidden="true" />
                        <span className="font-display text-5xl">{getInitials(m.name)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-7">
                    {m.office ? (
                      <div className="inline-flex items-center gap-1.5 text-xs text-accent uppercase tracking-[0.18em] font-medium mb-3">
                        <MapPin className="h-3 w-3" /> {m.office}
                      </div>
                    ) : null}
                    <h2 className="font-display text-2xl text-foreground">{m.name}</h2>
                    <p className="text-sm text-foreground-muted mt-1.5 leading-relaxed">{m.role}</p>
                    {m.phone || m.email ? (
                      <div className="mt-5 pt-5 border-t border-border space-y-2.5 text-sm">
                        {m.phone ? (
                          <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="flex items-center gap-2.5 text-foreground/85 hover:text-primary transition-colors">
                            <Phone className="h-3.5 w-3.5 text-primary" /> {m.phone}
                          </a>
                        ) : null}
                        {m.email ? (
                          <a href={`mailto:${m.email}`} className="flex items-center gap-2.5 text-foreground/85 hover:text-primary transition-colors break-all">
                            <Mail className="h-3.5 w-3.5 text-primary flex-shrink-0" /> {m.email}
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <RelatedLinksSection
        title="Utforska mer än bara teamet."
        description="Se hur vi arbetar, läs om våra kunder eller kontakta ett kontor för att få en personlig genomgång av ert behov."
        links={[
          {
            to: "/om-oss",
            title: "Om oss",
            body: "Läs mer om byrån, våra värderingar och vår lokala närvaro i Umeå, Stockholm och Vännäs.",
          },
          {
            to: "/kontakt-umea",
            title: "Kontakta Umeå",
            body: "Prata bokföring, löneadministration, bokslut eller rådgivning med vårt Umeåteam.",
          },
          {
            to: "/kontakt-stockholm",
            title: "Kontakta Stockholm",
            body: "Få kontakt med vårt team för företag i Stockholm och Mälardalen.",
          },
          {
            to: "/karriar",
            title: "Karriär",
            body: "Läs om hur det är att arbeta på en modern redovisningsbyrå.",
          },
        ]}
      />

      <CtaBand title="Vill du jobba här?" body="Läs mer om hur det är att arbeta hos oss på vår karriärsida." />
    </>
  );
};

export default Medarbetare;
